import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { CONCIERGE_SCOPE_LIST, MODULES } from '@yourstore/domain';
import { useAction, useApi, useConsole, useRead } from './context';
import { Failure, OnceBox, Section, StatePill, When } from './bits';
import { PARTNER_PATH_MODULES } from '../vocabulary';
import type { JobExecution, KeyIssued } from '../wire';

/**
 * The acceptance path, walked once: **a new tenant from nothing to taking
 * partner bookings in under an hour, entirely through the console** (plan,
 * MVP 5).
 *
 * The wizard is a guided path over the same endpoints, not a second API. Every
 * step calls exactly the method the tenant page calls; nothing here has a
 * shortcut the ordinary screens lack, and nothing here validates something the
 * server does not. What it adds is order and the sentence between steps — which
 * is the actual difficulty of onboarding, since the steps themselves are each
 * one request.
 *
 * ## Step 5 is the interesting one, and it is a wall on purpose
 *
 * A tenant is not bookable until it has a catalog, and **this console cannot
 * create one**. That is not an omission to fill in later: `admin_api` holds no
 * grant on `items`, `resources` or anything else in a tenant's book (migration
 * 0008), because spec §6.3 says the operator console is never a data browser —
 * and that cuts both ways. A surface that must not read a tenant's catalog must
 * not write one either; "never a data browser" would be a thin promise if the
 * same seat could seed the data it is not allowed to look at.
 *
 * So step 5 explains, points at the two places that *can* do it — the tenant's
 * own staff app, or `apps/cli`'s `catalog seed` — and then asks the operator to
 * confirm they have done it. **It does not poll for a check that does not
 * exist.** `GET /tenants/:id` carries no service count, precisely because
 * counting a tenant's services would be reading their book; a Recheck button
 * that re-read the tenant page and looked for services would be a button that
 * cannot tell success from failure, which is worse than no button. Saying "we
 * cannot see this from here, tell us when it is done" is the honest interface
 * to a boundary we chose.
 */

const STEPS = [
  'Create the tenant',
  'Enable modules',
  'Issue the partner key',
  'Point the webhook',
  'Seed the catalog',
  'Run conformance',
] as const;

export function Wizard({
  onDone,
  onCancel,
}: {
  onDone: (tenantId: string) => void;
  onCancel: () => void;
}): ReactNode {
  const [step, setStep] = useState(0);
  const [tenantId, setTenantId] = useState<string | null>(null);

  return (
    <div className="view">
      <header className="view__head">
        <div>
          <button type="button" className="linkish" onClick={onCancel}>
            ← Fleet
          </button>
          <h2>Onboard a tenant</h2>
        </div>
        {tenantId === null ? null : (
          <div className="view__actions">
            <button type="button" className="button" onClick={() => onDone(tenantId)}>
              Open tenant page
            </button>
          </div>
        )}
      </header>

      <ol className="stepper">
        {STEPS.map((label, i) => (
          <li
            key={label}
            className={`stepper__step${i === step ? ' is-current' : ''}${i < step ? ' is-done' : ''}`}
          >
            <span className="stepper__n">{i + 1}</span>
            {label}
          </li>
        ))}
      </ol>

      {step === 0 ? (
        <CreateStep
          onCreated={(id) => {
            setTenantId(id);
            setStep(1);
          }}
        />
      ) : null}

      {step > 0 && tenantId !== null ? (
        <>
          {step === 1 ? <ModulesStep tenantId={tenantId} onNext={() => setStep(2)} /> : null}
          {step === 2 ? <KeyStep tenantId={tenantId} onNext={() => setStep(3)} /> : null}
          {step === 3 ? <WebhookStep tenantId={tenantId} onNext={() => setStep(4)} /> : null}
          {step === 4 ? <CatalogStep tenantId={tenantId} onNext={() => setStep(5)} /> : null}
          {step === 5 ? (
            <ConformanceStep tenantId={tenantId} onFinish={() => onDone(tenantId)} />
          ) : null}

          {step > 0 && step < 5 ? (
            <div className="row-actions">
              <button type="button" className="button" onClick={() => setStep(step - 1)}>
                Back
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/* ---- 1. create --------------------------------------------------- */

function CreateStep({ onCreated }: { onCreated: (tenantId: string) => void }): ReactNode {
  const api = useApi();
  const action = useAction();
  const [tenantId, setTenantId] = useState('t_');
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('America/St_Thomas');
  const [currency, setCurrency] = useState('USD');
  const [plan, setPlan] = useState('standard');

  const submit = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.createTenant({
        tenant_id: tenantId,
        name,
        timezone,
        currency,
        plan,
      });
      onCreated(answer.tenant_id);
    });
    if (!ok) return;
  };

  return (
    <Section
      title="1 · Create the tenant"
      note="The id is an operator-chosen slug and is never reused. It starts as onboarding: authority reports approximate until you flip it to live, which is the cutover and is not a wizard step."
    >
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="form__row">
          <label className="field">
            <span className="field__label">Tenant id</span>
            <input
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              aria-label="Tenant id"
            />
            <span className="field__hint">
              <code>t_</code>, then lower-case letters, digits and underscores —{' '}
              <code>t_lovespa_frenchtown</code>.
            </span>
          </label>
          <label className="field field--wide">
            <span className="field__label">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Name" />
          </label>
        </div>
        <div className="form__row">
          <label className="field">
            <span className="field__label">Timezone</span>
            <input
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              aria-label="Timezone"
            />
            <span className="field__hint">
              Full IANA name. Availability is computed in it — a wrong one is a booking an hour off.
            </span>
          </label>
          <label className="field">
            <span className="field__label">Currency</span>
            <input
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              aria-label="Currency"
            />
          </label>
          <label className="field">
            <span className="field__label">Plan</span>
            <input value={plan} onChange={(e) => setPlan(e.target.value)} aria-label="Plan" />
          </label>
        </div>

        {action.failure !== null ? <Failure failure={action.failure} /> : null}

        <div className="row-actions">
          <button type="submit" className="button button--primary" disabled={action.busy}>
            {action.busy ? 'Creating…' : 'Create tenant'}
          </button>
        </div>
      </form>
    </Section>
  );
}

/* ---- 2. modules -------------------------------------------------- */

function ModulesStep({ tenantId, onNext }: { tenantId: string; onNext: () => void }): ReactNode {
  const api = useApi();
  const action = useAction();
  const [picked, setPicked] = useState<string[]>([...PARTNER_PATH_MODULES]);
  const [enabled, setEnabled] = useState<string[] | null>(null);

  const submit = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.setModules(tenantId, { enable: picked });
      setEnabled(answer.enabled);
    });
    if (ok) onNext();
  };

  return (
    <Section
      title="2 · Enable modules"
      note="Dependencies are validated against the state this request would leave behind, so the whole partner path goes in one call. Ask for partner_api without calendar and the server refuses with the fix in the sentence — that refusal is shown here rather than pre-empted."
    >
      <fieldset className="scopes">
        <legend>Modules</legend>
        {MODULES.filter((m) => m.kind !== 'platform').map((m) => (
          <label key={m.key} className="scope">
            <input
              type="checkbox"
              checked={picked.includes(m.key)}
              onChange={(e) =>
                setPicked((list) =>
                  e.target.checked ? [...list, m.key] : list.filter((x) => x !== m.key),
                )
              }
            />
            <code>{m.key}</code>
            <span className="muted"> {m.label}</span>
            {m.dependsOn.length > 0 ? (
              <span className="muted"> — needs {m.dependsOn.join(', ')}</span>
            ) : null}
          </label>
        ))}
      </fieldset>

      {action.failure !== null ? <Failure failure={action.failure} /> : null}
      {enabled !== null ? <p className="ok">Enabled: {enabled.join(', ')}</p> : null}

      <div className="row-actions">
        <button
          type="button"
          className="button button--primary"
          onClick={() => void submit()}
          disabled={action.busy || picked.length === 0}
        >
          {action.busy ? 'Applying…' : 'Enable and continue'}
        </button>
      </div>
    </Section>
  );
}

/* ---- 3. the key -------------------------------------------------- */

function KeyStep({ tenantId, onNext }: { tenantId: string; onNext: () => void }): ReactNode {
  const api = useApi();
  const action = useAction();
  const [integration, setIntegration] = useState('aiconcierge');
  const [issued, setIssued] = useState<KeyIssued | null>(null);

  const submit = async (): Promise<void> => {
    await action.run(async () => {
      const answer = await api.issueKey(tenantId, {
        integration,
        env: 'test',
        scopes: [...CONCIERGE_SCOPE_LIST],
      });
      setIssued(answer);
    });
  };

  return (
    <Section
      title="3 · Issue the partner key"
      note="A test key with the concierge's five scopes (spec §3.3) — no clients:write, nothing from products, sales, certificates, credit, billing or reporting."
    >
      {issued === null ? (
        <>
          <label className="field">
            <span className="field__label">Integration</span>
            <input
              value={integration}
              onChange={(e) => setIntegration(e.target.value)}
              aria-label="Integration"
            />
          </label>
          <p className="muted">
            Scopes: {CONCIERGE_SCOPE_LIST.map((s) => s).join(', ')}. Environment: <code>test</code>{' '}
            — the credential names its own environment, so an incident never has to guess.
          </p>

          {action.failure !== null ? <Failure failure={action.failure} /> : null}

          <div className="row-actions">
            <button
              type="button"
              className="button button--primary"
              onClick={() => void submit()}
              disabled={action.busy}
            >
              {action.busy ? 'Issuing…' : 'Issue key'}
            </button>
          </div>
        </>
      ) : (
        <>
          <OnceBox
            label={`${issued.prefix}… — ${issued.integration} (${issued.env})`}
            value={issued.plaintext}
            note={issued.plaintext_note}
            onDismiss={() => {
              // Dismissing drops the plaintext and advances: there is nothing
              // left to show, and a step that could show it again would be a
              // step that kept it.
              setIssued(null);
              onNext();
            }}
          />
          {issued.warnings.map((w) => (
            <p key={w} className="warn">
              {w}
            </p>
          ))}
        </>
      )}
    </Section>
  );
}

/* ---- 4. the webhook ---------------------------------------------- */

function WebhookStep({ tenantId, onNext }: { tenantId: string; onNext: () => void }): ReactNode {
  const api = useApi();
  const action = useAction();
  const [url, setUrl] = useState('');
  const [secretRef, setSecretRef] = useState('');
  const [done, setDone] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const ok = await action.run(async () => {
      const answer = await api.setWebhook(tenantId, {
        integration: 'aiconcierge',
        url,
        secret_ref: secretRef,
      });
      setDone(`${answer.created ? 'Created' : 'Updated'} → ${answer.url}`);
    });
    if (ok) onNext();
  };

  return (
    <Section
      title="4 · Point the webhook"
      note="Where booking events go. https only; the secret reference is a Secret Manager resource name, never the secret — apps/notifications resolves it at call time."
    >
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <label className="field field--wide">
          <span className="field__label">URL</span>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            aria-label="URL"
            placeholder="https://partner.example/hooks/yourstore"
          />
        </label>
        <label className="field field--wide">
          <span className="field__label">Secret reference</span>
          <input
            value={secretRef}
            onChange={(e) => setSecretRef(e.target.value)}
            aria-label="Secret reference"
            placeholder="projects/yourstore-dev/secrets/partner-webhook-secret/versions/latest"
          />
        </label>

        {action.failure !== null ? <Failure failure={action.failure} /> : null}
        {done !== null ? <p className="ok">{done}</p> : null}

        <div className="row-actions">
          <button type="button" className="button" onClick={onNext}>
            Skip for now
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={action.busy || url === '' || secretRef === ''}
          >
            {action.busy ? 'Saving…' : 'Save and continue'}
          </button>
        </div>
      </form>
    </Section>
  );
}

/* ---- 5. the catalog, which this console cannot seed --------------- */

function CatalogStep({ tenantId, onNext }: { tenantId: string; onNext: () => void }): ReactNode {
  const { config } = useConsole();
  const [confirmed, setConfirmed] = useState(false);
  const staffUrl = config.staffAppUrlPattern;

  return (
    <Section
      title="5 · Seed the catalog — not from here"
      note="This step is a boundary, not a gap."
    >
      <div className="by-design" role="note">
        <h4>The operator console cannot create a catalog, and will not learn to</h4>
        <p>
          A tenant is not bookable until it has services, resources and hours. This seat holds no
          grant on any of them: spec §6.3 says the operator console is never a data browser, and a
          surface that must not read a tenant’s book must not write one either.
        </p>
        <p className="by-design__rule">
          There is also no check to offer. <code>GET /tenants/{tenantId}</code> carries no service
          count — counting a tenant’s services would be reading their book — so a “Recheck” button
          here could not tell success from failure. Rather than fake one, this step asks you.
        </p>
      </div>

      <p>Two places can do it, and both are audited:</p>
      <ul className="plain-list">
        <li>
          <strong>The owner, in their own staff app</strong> — Settings → services, resources and
          hours, signed in as themselves at{' '}
          <a href={`${staffUrl}`} target="_blank" rel="noreferrer">
            {staffUrl}
          </a>
          . This is the path that scales, because it is the person who knows the menu.
        </li>
        <li>
          <strong>The CLI</strong> —{' '}
          <code>pnpm --filter @yourstore/cli cli -- catalog seed --tenant {tenantId} …</code>, for a
          migration or a demo tenant.
        </li>
      </ul>

      <label className="scope">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
        />
        <span>
          The catalog for <code>{tenantId}</code> has been seeded. I have checked it in the staff
          app or the CLI, not here.
        </span>
      </label>

      <div className="row-actions">
        <button
          type="button"
          className="button button--primary"
          onClick={onNext}
          disabled={!confirmed}
        >
          Continue
        </button>
      </div>
    </Section>
  );
}

/* ---- 6. conformance ---------------------------------------------- */

/**
 * Run the suite and watch it, because "conformance green at the end of it" is
 * the acceptance sentence and an operator should see it turn green rather than
 * be told it will.
 *
 * The poll is the execution endpoint every five seconds while the state is
 * `running`, and it stops on its own — a console that kept polling a finished
 * job would be a console writing an `audit_log` row every five seconds forever.
 */
function ConformanceStep({
  tenantId,
  onFinish,
}: {
  tenantId: string;
  onFinish: () => void;
}): ReactNode {
  const api = useApi();
  const action = useAction();
  const [executionId, setExecutionId] = useState<string | null>(null);
  const [execution, setExecution] = useState<JobExecution | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const tenant = useRead(() => api.tenant(tenantId), [tenantId]);

  const start = async (): Promise<void> => {
    await action.run(async () => {
      const answer = await api.runJob('conformance', { tenant_id: tenantId });
      setNote(answer.note);
      if (answer.execution !== null) {
        setExecutionId(answer.execution.execution_id);
        setExecution(answer.execution);
      }
    });
  };

  useEffect(() => {
    if (executionId === null) return;
    if (execution !== null && execution.state !== 'running' && execution.state !== 'unknown') {
      return;
    }
    let alive = true;
    const timer = setInterval(() => {
      void api
        .execution('conformance', executionId)
        .then((answer) => {
          if (alive) setExecution(answer.execution);
        })
        .catch(() => {
          // A poll that fails is not a run that failed. The last state stays on
          // screen and the next tick tries again.
        });
    }, 5000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [api, executionId, execution]);

  const status = tenant.value?.tenant.status ?? null;

  return (
    <Section
      title="6 · Run conformance"
      note="The ~70 obligations of the agreement, against this tenant. Green here is what MVP 1's definition of done means, and it is the acceptance sentence for this whole path."
    >
      {status !== null && status !== 'live' ? (
        <p className="warn">
          {tenantId} is <code>{status}</code>, so availability reports <code>approximate</code> and
          the checks that require <code>authority: "live"</code> will not pass. Flipping to{' '}
          <code>live</code> is the cutover — it is done from the tenant page, deliberately not as a
          click at the end of a wizard.
        </p>
      ) : null}

      {action.failure !== null ? <Failure failure={action.failure} /> : null}
      {note !== null ? <p className="muted">{note}</p> : null}

      {execution === null ? null : (
        <dl className="facts">
          <div>
            <dt>Execution</dt>
            <dd>
              <code>{execution.execution_id}</code>
            </dd>
          </div>
          <div>
            <dt>State</dt>
            <dd>
              <StatePill state={execution.state} />
            </dd>
          </div>
          <div>
            <dt>Tasks</dt>
            <dd>
              {execution.succeeded_count ?? 0}/{execution.task_count ?? 0}
              {(execution.failed_count ?? 0) > 0 ? ` — ${execution.failed_count} failed` : ''}
            </dd>
          </div>
          <div>
            <dt>Completed</dt>
            <dd>
              <When iso={execution.completed_at} empty="running" />
            </dd>
          </div>
        </dl>
      )}

      {execution?.state === 'succeeded' ? (
        <p className="ok">
          Green. {tenantId} answers every obligation the agreement makes — it can take partner
          bookings.
        </p>
      ) : null}
      {execution?.state === 'failed' ? (
        <p className="warn">
          Red. The failing checks are in this execution’s logs in Cloud Logging; fix and run again.
        </p>
      ) : null}

      <div className="row-actions">
        <button
          type="button"
          className="button button--primary"
          onClick={() => void start()}
          disabled={action.busy}
        >
          {action.busy ? 'Starting…' : execution === null ? 'Run conformance' : 'Run again'}
        </button>
        <button type="button" className="button" onClick={onFinish}>
          Finish — open the tenant page
        </button>
      </div>
    </Section>
  );
}
